import { debounce } from '@utils/debounce';

interface AnimatedWebpConfig {
  threshold: number;
  rootMargin: string;
}

const createAnimatedWebpImage = (config: Partial<AnimatedWebpConfig> = {}) => {
  let observer: IntersectionObserver | null = null;
  let isMobile: boolean;
  let isLandscape: boolean;
  let currentlyAnimatedElement: HTMLElement | null = null;

  const defaultConfig: AnimatedWebpConfig = {
    threshold: 1.0, // Celý obrázek musí být viditelný
    rootMargin: '-80px 0px 0px 0px', // 70px od horního okraje
    ...config,
  };

  const SELECTORS = {
    animatedWebp: '.global-image__animated-webp',
    staticImage: '.global-image__img--has-animated-webp',
    container: '.global-image__container--has-animated-webp',
  };

  const detectMobile = (): boolean => {
    return window.innerWidth <= 679;
  };

  const detectLandscape = (): boolean => {
    return window.innerWidth > window.innerHeight;
  };

  const showAnimatedWebp = (
    animatedWebp: HTMLImageElement,
    staticImage: HTMLImageElement,
    container: HTMLElement
  ): void => {
    // Skrýt předchozí animovaný element, pokud existuje
    if (currentlyAnimatedElement && currentlyAnimatedElement !== container) {
      const prevAnimatedWebp = currentlyAnimatedElement.querySelector(
        SELECTORS.animatedWebp
      ) as HTMLImageElement;
      const prevStaticImage = currentlyAnimatedElement.querySelector(
        SELECTORS.staticImage
      ) as HTMLImageElement;

      if (prevAnimatedWebp && prevStaticImage) {
        prevAnimatedWebp.style.opacity = '0';
        prevStaticImage.style.opacity = '1';
      }
    }

    animatedWebp.style.opacity = '1';
    staticImage.style.opacity = '0';
    currentlyAnimatedElement = container;
  };

  const hideAnimatedWebp = (
    animatedWebp: HTMLImageElement,
    staticImage: HTMLImageElement
  ): void => {
    animatedWebp.style.opacity = '0';
    staticImage.style.opacity = '1';
  };

  const shouldShowAnimatedWebp = (entry: IntersectionObserverEntry): boolean => {
    if (isLandscape) {
      return false;
    }

    const rect = entry.boundingClientRect;
    const elementBottom = rect.bottom;
    const distanceFromBottom = window.innerHeight - elementBottom;

    return distanceFromBottom > 10;
  };

  const handleIntersection = (entry: IntersectionObserverEntry): void => {
    const container = entry.target as HTMLElement;
    const animatedWebp = container.querySelector(SELECTORS.animatedWebp) as HTMLImageElement;
    const staticImage = container.querySelector(SELECTORS.staticImage) as HTMLImageElement;

    if (!animatedWebp || !staticImage) {
      return;
    }

    const shouldShow = shouldShowAnimatedWebp(entry);

    if (entry.isIntersecting && shouldShow) {
      showAnimatedWebp(animatedWebp, staticImage, container);
    } else {
      hideAnimatedWebp(animatedWebp, staticImage);
      // Reset currently animated element if this was the one
      if (currentlyAnimatedElement === container) {
        currentlyAnimatedElement = null;
      }
    }
  };

  const setupIntersectionObserver = (): void => {
    const options: IntersectionObserverInit = {
      threshold: defaultConfig.threshold,
      rootMargin: defaultConfig.rootMargin,
    };

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        handleIntersection(entry);
      });
    }, options);

    // Najít všechny animované WebP obrázky
    const animatedImages = document.querySelectorAll(SELECTORS.container);
    animatedImages.forEach((image) => {
      observer?.observe(image);
    });
  };

  const setupOrientationListener = (): void => {
    const handleResize = () => {
      const newIsMobile = detectMobile();
      const newIsLandscape = detectLandscape();

      if (newIsMobile !== isMobile || newIsLandscape !== isLandscape) {
        isMobile = newIsMobile;
        isLandscape = newIsLandscape;

        // Restartovat observer při změně orientace
        if (observer) {
          observer.disconnect();
          setupIntersectionObserver();
        }

        // Reset currently animated element při změně orientace
        currentlyAnimatedElement = null;
      }
    };

    const debouncedHandleResize = debounce(handleResize, 250);

    window.addEventListener('resize', debouncedHandleResize);
    window.addEventListener('orientationchange', debouncedHandleResize);
  };

  const init = (): void => {
    isMobile = detectMobile();
    isLandscape = detectLandscape();

    if (!isMobile) {
      return;
    }

    setupIntersectionObserver();
    setupOrientationListener();
  };

  const destroy = (): void => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  const updateConfig = (newConfig: Partial<AnimatedWebpConfig>): void => {
    Object.assign(defaultConfig, newConfig);

    if (observer) {
      observer.disconnect();
      setupIntersectionObserver();
    }
  };

  // Spustit inicializaci
  init();

  return {
    destroy,
    updateConfig,
  };
};

// Inicializace při načtení DOM
export const initAnimatedWebpImages = (): void => {
  createAnimatedWebpImage();
};

// Export pro použití v jiných modulech
export default createAnimatedWebpImage;
